/**
 * BudgetUtilizationWidget — CFSS budget utilization tracking
 *
 * MN 256B.85: Participant-directed budgets
 * Shows budget allocation, spending, and remaining balance.
 */

import React from 'react';
import {
  Card,
  Group,
  Progress,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconCash,
  IconChartPie,
  IconTrendingDown,
  IconTrendingUp,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

const GET_BUDGET_DATA = gql`
  query GetBudgetData($limit: Int) {
    payrollTimesheets(status: APPROVED, limit: $limit) {
      id
      totalRegularHours
      totalOvertimeHours
      payPeriodStart
      payPeriodEnd
    }
  }
`;

interface Timesheet {
  id: string;
  totalRegularHours: number;
  totalOvertimeHours: number;
  payPeriodStart: string;
  payPeriodEnd: string;
}

interface BudgetUtilizationWidgetProps {
  title?: string;
  compact?: boolean;
  annualBudget?: number;
  hourlyRate?: number;
}

export function BudgetUtilizationWidget({
  title = 'Budget Utilization',
  compact = false,
  annualBudget = 50000,
  hourlyRate = 18.50,
}: BudgetUtilizationWidgetProps) {
  const { data, loading } = useQuery<{ payrollTimesheets: Timesheet[] }>(GET_BUDGET_DATA, {
    variables: { limit: 500 },
  });

  const timesheets = data?.payrollTimesheets ?? [];

  // Calculate totals
  const totalRegularHours = timesheets.reduce((sum, ts) => sum + ts.totalRegularHours, 0);
  const totalOvertimeHours = timesheets.reduce((sum, ts) => sum + ts.totalOvertimeHours, 0);
  const totalHours = totalRegularHours + totalOvertimeHours;

  // Estimate spending (regular rate + 1.5x OT)
  const regularSpend = totalRegularHours * hourlyRate;
  const overtimeSpend = totalOvertimeHours * hourlyRate * 1.5;
  const totalSpend = regularSpend + overtimeSpend;
  const remaining = annualBudget - totalSpend;
  const utilizationPercent = Math.round((totalSpend / annualBudget) * 100);

  // Project monthly spend rate
  const now = new Date();
  const monthsElapsed = now.getMonth() + 1;
  const monthlyRate = totalSpend / monthsElapsed;
  const projectedAnnual = monthlyRate * 12;
  const onTrack = projectedAnnual <= annualBudget;

  if (loading) {
    return (
      <Card withBorder padding="lg">
        <Text size="sm" c="dimmed">Loading budget data...</Text>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card withBorder padding="md">
        <Group justify="space-between">
          <Stack gap={0}>
            <Text size="sm" c="dimmed">{title}</Text>
            <Text size="xl" fw={700}>${totalSpend.toLocaleString()}</Text>
            <Text size="xs" c={remaining > 0 ? 'green' : 'red'}>
              ${Math.abs(remaining).toLocaleString()} {remaining >= 0 ? 'remaining' : 'over'}
            </Text>
          </Stack>
          <RingProgress
            size={60}
            thickness={6}
            roundCaps
            sections={[
              { value: Math.min(utilizationPercent, 100), color: utilizationPercent > 90 ? 'red' : utilizationPercent > 75 ? 'yellow' : 'blue' },
            ]}
          />
        </Group>
      </Card>
    );
  }

  return (
    <Card withBorder padding="lg">
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="green">
            <IconCash size={14} />
          </ThemeIcon>
          <Text fw={600}>{title}</Text>
        </Group>
        <Tooltip label={`${utilizationPercent}% of annual budget used`}>
          <RingProgress
            size={56}
            thickness={5}
            roundCaps
            sections={[
              { value: Math.min(utilizationPercent, 100), color: utilizationPercent > 90 ? 'red' : utilizationPercent > 75 ? 'yellow' : 'blue' },
            ]}
            label={
              <Text size="xs" ta="center" fw={700}>
                {utilizationPercent}%
              </Text>
            }
          />
        </Tooltip>
      </Group>

      <SimpleGrid cols={2} mb="md">
        <Stack gap={2}>
          <Text size="xs" c="dimmed">Spent</Text>
          <Text size="lg" fw={700}>${totalSpend.toLocaleString()}</Text>
          <Group gap={4}>
            <Text size="xs" c="dimmed">{totalHours.toFixed(0)} hrs</Text>
            {totalOvertimeHours > 0 && (
              <Text size="xs" c="orange">({totalOvertimeHours.toFixed(0)} OT)</Text>
            )}
          </Group>
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed">Remaining</Text>
          <Text size="lg" fw={700} c={remaining >= 0 ? 'green' : 'red'}>
            ${Math.abs(remaining).toLocaleString()}
          </Text>
          <Text size="xs" c="dimmed">
            of ${annualBudget.toLocaleString()} annual
          </Text>
        </Stack>
      </SimpleGrid>

      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Budget Progress</Text>
          <Group gap={4}>
            <ThemeIcon size="xs" variant="light" color={onTrack ? 'green' : 'red'}>
              {onTrack ? <IconTrendingDown size={10} /> : <IconTrendingUp size={10} />}
            </ThemeIcon>
            <Text size="xs" c={onTrack ? 'green' : 'red'}>
              {onTrack ? 'On track' : 'Over budget pace'}
            </Text>
          </Group>
        </Group>
        <Progress.Root size="lg">
          <Progress.Section
            value={(regularSpend / annualBudget) * 100}
            color="blue"
          >
            <Progress.Label>Regular</Progress.Label>
          </Progress.Section>
          <Progress.Section
            value={(overtimeSpend / annualBudget) * 100}
            color="orange"
          >
            <Progress.Label>OT</Progress.Label>
          </Progress.Section>
        </Progress.Root>
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            Monthly avg: ${monthlyRate.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </Text>
          <Text size="xs" c="dimmed">
            Projected: ${projectedAnnual.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
}

export default BudgetUtilizationWidget;
